import { useCallback, useEffect, useState } from 'react'
import { hasModifierKey } from 'utils/hasModifierKey'

interface Props {
  enabled?: boolean
  rowsCount: number
  onSelect: (index: number, withModifier?: boolean) => void
  onCancel: () => void
  initialSelection?: number
  enableEnter?: boolean
}

export const useScrollableTable = ({
  enabled = true,
  enableEnter = true,
  rowsCount,
  onSelect,
  onCancel,
  initialSelection = 0,
}: Props) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(initialSelection)

  useEffect(() => {
    setSelectedIndex(initialSelection)
  }, [initialSelection, rowsCount])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) {
        return
      }
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'].includes(event.key)) {
        onCancel()
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        event.stopPropagation()

        if (selectedIndex === null) {
          setSelectedIndex(rowsCount)
        } else if (selectedIndex > 0) {
          setSelectedIndex(selectedIndex - 1)
        }
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        event.stopPropagation()

        if (selectedIndex === null) {
          setSelectedIndex(0)
        } else if (selectedIndex === rowsCount) {
          return
        } else if (selectedIndex < rowsCount) {
          setSelectedIndex(selectedIndex + 1)
        }
      }

      if (event.key === 'Enter' && enableEnter) {
        event.preventDefault()
        event.stopPropagation()

        if (selectedIndex !== null) {
          onSelect(selectedIndex, hasModifierKey(event))
        }
      }
    },
    [enabled, enableEnter, onCancel, selectedIndex, rowsCount, onSelect],
  )

  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) {
        return
      }

      if (event.key === 'Escape') {
        event.preventDefault()

        onCancel()
      }
    },
    [enabled, onCancel],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', handleKeyUp)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [handleKeyDown, handleKeyUp])

  return {
    selectedIndex,
    setSelectedIndex,
  }
}
